import React, { useCallback, useEffect } from 'react';
import { Button, Modal, Title } from 'ui-library';
import { getClassNames } from 'utils';
import { useTranslation } from 'react-i18next';
import arrayContains from 'utils/arrayUtils';
import { Product } from 'core/types/api/Api';
import { PRODUCTS, strategyTypes } from '../../../../../constants/constants';

// --- Interfaces ---

export interface StrategyItem {
    Id: number | string;
    Name: string;
    Type?: string;
    Products: (number | string)[];
    [key: string]: any;
}

export interface ProductItem extends Product {
    Id: number | string;
    RiskScore: number;
    [key: string]: any;
}

interface StrategyListProps {
    strategies?: StrategyItem[];
    selectedStrategyId?: number | string;
    setSelectedStrategyId?: (id: number | string | undefined) => void;
    recommendedStrategy?: Partial<StrategyItem>;
    products?: ProductItem[];
    cachedSelectStrategy?: {
        strategyId?: number | string;
    };
    selectedProductKey?: string;
    disabled?: boolean;
}

// --- Component ---

const StrategyList: React.FC<StrategyListProps> = ({
    strategies = [],
    selectedStrategyId = undefined,
    setSelectedStrategyId = () => {},
    recommendedStrategy = {},
    products = [],
    cachedSelectStrategy,
    selectedProductKey,
    disabled,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        const strategyId = cachedSelectStrategy?.strategyId || recommendedStrategy?.Id;

        setSelectedStrategyId(strategyId);
    }, [recommendedStrategy?.Id, cachedSelectStrategy?.strategyId, setSelectedStrategyId]);

    const onStrategyClick = useCallback((strategyId: number | string) => () => {
        const recStrategy = strategies.find(item => item.Id === recommendedStrategy?.Id);

        // Find product with highest RiskScore within the recommended strategy
        const recommendedProduct = [...products]
            .sort((a, b) => b.RiskScore - a.RiskScore)
            .filter(item => recStrategy?.Products.includes(item.Id))[0];

        const selectedStrategy = strategies.find(item => item.Id === strategyId);

        // Find product with highest RiskScore within the newly selected strategy
        const selectedProductByScore = [...products]
            .sort((a, b) => b.RiskScore - a.RiskScore)
            .filter(item => selectedStrategy?.Products.includes(item.Id))[0];

        if (selectedStrategy?.Type === strategyTypes.INDIVIDUAL) {
            setSelectedStrategyId(strategyId);
        } else if (selectedProductByScore) {
            const isNewlySelectedRiskHigher = selectedProductByScore.RiskScore > (recommendedProduct?.RiskScore || 0);
            const isNewlySelectedRiskLower = selectedProductByScore.RiskScore < (recommendedProduct?.RiskScore || 0);

            if (isNewlySelectedRiskHigher) {
                if (selectedProductKey && arrayContains([PRODUCTS.fzp], selectedProductKey)) {
                    Modal.confirm({
                        title: t('portfolio.disclaimer'),
                        content: t('portfolio.createNewPortfolio.strategy.fzpHigherRiskNotAllowed'),
                        cancelText: false,
                    });
                } else {
                    Modal.confirm({
                        title: t('portfolio.disclaimer'),
                        content: t('portfolio.createNewPortfolio.strategy.disclaimerText.high'),
                        okText: t('clientDashboard.confirm'),
                        onOk: async () => {
                            setSelectedStrategyId(strategyId);
                        },
                        cancelText: false,
                    });
                }

                return;
            }

            if (isNewlySelectedRiskLower) {
                Modal.confirm({
                    title: t('portfolio.disclaimer'),
                    content: t('portfolio.createNewPortfolio.strategy.disclaimerText.low'),
                    okText: t('clientDashboard.confirm'),
                    onOk: async () => {
                        setSelectedStrategyId(strategyId);
                    },
                    cancelText: false,
                });

                return;
            }

            setSelectedStrategyId(strategyId);
        }
    }, [strategies, recommendedStrategy, setSelectedStrategyId, selectedProductKey, products, t]);

    return (
        <div className="fi-strategy-selection buttonRadioElementWrapper">
            <Title type={2}>{t('portfolio.createNewPortfolio.strategy')}</Title>
            <div className="new-portfolio-content form-group-controls">
                {strategies.map((strategyItem) => (
                    <Button
                        disabled={disabled}
                        key={strategyItem.Id}
                        type={selectedStrategyId === strategyItem.Id ? 'primary' : 'secondary'}
                        size="small"
                        onClick={onStrategyClick(strategyItem.Id)}
                        className={getClassNames(
                            'buttonRadioElement',
                            selectedStrategyId === strategyItem.Id ? 'buttonRadioElementNotSelected' : '',
                            recommendedStrategy.Id === strategyItem.Id ? 'buttonRadioRecommended' : '',
                        )}
                    >
                        {strategyItem?.Name}
                        {' '}
                        {recommendedStrategy.Id === strategyItem.Id && (
                            <span>
                                (
                                {t('portfolio.createNewPortfolio.strategy.recommended')}
                                )
                            </span>
                        )}
                    </Button>
                ))}
            </div>
        </div>
    );
};

export default StrategyList;
